import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { LLC, isLLCFormed } from "../legal";

/**
 * Letter of Listing — printable HTML page a manufacturer can save as PDF
 * and forward to US buyers as proof they are a verified member of the
 * Corridor Digital registry. Read-only, auto-fills from their profile.
 */
export default function LetterOfListing() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/manufacturers/me");
        setP(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="container-cd py-20 overline">Loading…</div>;
  }
  if (!p) {
    return <div className="container-cd py-20 overline">Profile not found.</div>;
  }

  const C = COPY[lang];
  const today = new Date().toISOString().slice(0, 10);
  const verified = !!p.verified;
  const certs = (p.certifications || []).filter(Boolean);
  const caps = (p.capabilities || []).slice(0, 6);

  return (
    <div data-testid="letter-of-listing-page" className="letter">
      {/* Toolbar — hidden on print */}
      <div className="no-print" style={{ borderBottom: "1px solid var(--line)", background: "var(--surface)" }}>
        <div className="container-cd py-4 flex items-center justify-between gap-4">
          <Link to="/dashboard" className="btn btn-ghost btn-sm">
            <ArrowLeft size={12} /> {C.back}
          </Link>
          <button onClick={() => window.print()} className="btn btn-primary btn-sm" data-testid="letter-print">
            <Printer size={12} /> {C.print}
          </button>
        </div>
      </div>

      <article className="letter-page">
        <div className="container-cd py-12 md:py-16 max-w-3xl">
          {/* Letterhead */}
          <div className="flex items-center justify-between pb-6" style={{ borderBottom: "2px solid var(--ink)" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center font-display text-white" style={{ background: "var(--ink)" }}>CD</div>
              <div className="leading-tight">
                <div className="font-display text-base">CORRIDOR DIGITAL</div>
                <div className="overline" style={{ fontSize: 9 }}>CHIHUAHUA ↔ EL PASO · IMMEX DIRECTORY OF RECORD</div>
              </div>
            </div>
            <div className="text-right font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              {C.letterHeader}<br />
              {today}
            </div>
          </div>

          <div className="mt-10 overline">{C.kicker}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-3 leading-[1.05]">{C.title}</h1>

          <div className="mt-8 cd-card p-5" style={{ background: "var(--surface)" }}>
            <div className="overline">{C.subjectLabel}</div>
            <div className="mt-2 font-heading text-2xl">{p.company_name}</div>
            {p.legal_name ? (
              <div className="mt-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                {C.legalName}: {p.legal_name}
              </div>
            ) : null}
            <div className="mt-1 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
              {p.city}{p.state ? `, ${p.state}` : ""} · {p.country || "MX"}
              {p.year_founded ? ` · ${C.founded} ${p.year_founded}` : ""}
              {p.plant_size_sqm ? ` · ${p.plant_size_sqm.toLocaleString()} m²` : ""}
              {p.employees ? ` · ${p.employees} ${C.employees}` : ""}
            </div>
          </div>

          <p className="mt-8 text-base leading-relaxed">
            {C.body1.replace("{name}", p.company_name)}
          </p>

          <p className="mt-4 text-base leading-relaxed">
            {C.body2}
          </p>

          <div className="mt-8 grid grid-cols-2 gap-6">
            <div>
              <div className="overline">{C.entityLabel}</div>
              <div className="mt-3 space-y-1 font-mono text-xs">
                {p.rfc ? <div>RFC: {maskRFC(p.rfc)}</div> : null}
                {p.legal_name ? <div>{C.legalName}: {p.legal_name}</div> : null}
                {p.immex_number ? <div>IMMEX #: {p.immex_number}</div> : null}
                {p.padron_number ? <div>{C.padron}: {p.padron_number}</div> : null}
                {p.duns_number ? <div>D-U-N-S: {p.duns_number}</div> : null}
                {!p.rfc && !p.legal_name && !p.immex_number && !p.padron_number && !p.duns_number ? (
                  <div style={{ color: "var(--ink-2)" }}>—</div>
                ) : null}
              </div>
            </div>
            <div>
              <div className="overline">{C.statusLabel}</div>
              <div className="mt-3 space-y-1 font-mono text-xs">
                <div>{verified ? `✓ ${C.statusVerified}` : `○ ${C.statusUnverified}`}</div>
                <div>{C.tier}: {(p.subscription_tier || "free").toUpperCase()}</div>
                <div>{C.published}: {p.published ? "✓" : "—"}</div>
                <div>{C.profileUrl}: corridordigital.net/m/{p.slug}</div>
              </div>
            </div>
          </div>

          {caps.length > 0 && (
            <div className="mt-8">
              <div className="overline">{C.capLabel}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {caps.map((c, i) => <span key={i} className="cd-chip">{c}</span>)}
              </div>
            </div>
          )}

          {certs.length > 0 && (
            <div className="mt-6">
              <div className="overline">{C.certsLabel}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {certs.map((c, i) => <span key={i} className="cd-chip cd-chip-dark">✓ {c}</span>)}
              </div>
            </div>
          )}

          <p className="mt-10 text-base leading-relaxed">{C.closing}</p>

          {/* Signature block */}
          <div className="mt-12 grid grid-cols-2 gap-12">
            <div>
              <div style={{ borderTop: "1px solid var(--ink)" }} className="pt-2 font-mono text-xs">
                Derick Varela
                <br /><span style={{ color: "var(--ink-2)" }}>{C.signerRole}</span>
              </div>
            </div>
            <div>
              <div style={{ borderTop: "1px solid var(--ink)" }} className="pt-2 font-mono text-xs">
                {LLC.contact_email}
                <br /><span style={{ color: "var(--ink-2)" }}>{C.entityFooter}: {isLLCFormed ? LLC.name : LLC.pending_label}</span>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 font-mono text-xs text-center" style={{ borderTop: "1px solid var(--line)", color: "var(--ink-2)" }}>
            {C.footnote}
          </div>
        </div>
      </article>

      <style>{`
        .letter-page { background: white; }
        @media print {
          .no-print { display: none !important; }
          header, footer, [data-testid="site-footer"], [data-testid="main-nav"], [data-testid="cookie-banner"] { display: none !important; }
          body { background: white !important; }
          @page { size: A4; margin: 16mm; }
        }
      `}</style>
    </div>
  );
}

function maskRFC(rfc) {
  if (!rfc) return "";
  const clean = String(rfc).replace(/\s+/g, "");
  if (clean.length <= 6) return clean;
  return clean.slice(0, 4) + "·".repeat(Math.max(0, clean.length - 7)) + clean.slice(-3);
}

const COPY = {
  en: {
    back: "Back to dashboard",
    print: "Print / save PDF",
    letterHeader: "OFFICIAL LETTER OF LISTING",
    kicker: "REF · CD-LOL-" + new Date().getFullYear(),
    title: "Letter of Listing — Corridor Digital registry.",
    subjectLabel: "SUBJECT",
    legalName: "Razón Social",
    founded: "Founded",
    employees: "employees",
    entityLabel: "LEGAL ENTITY",
    statusLabel: "PLATFORM STATUS",
    statusVerified: "Corridor Verified",
    statusUnverified: "Listing in progress",
    tier: "Tier",
    published: "Published",
    profileUrl: "Profile URL",
    capLabel: "DECLARED CAPABILITIES",
    certsLabel: "DECLARED CERTIFICATIONS",
    padron: "Padrón #",
    body1:
      "To whom it may concern — this letter confirms that {name} is a listed member of the Corridor Digital registry, the bilingual IMMEX directory of record for the Chihuahua–El Paso manufacturing corridor (Juárez, Parral, Chihuahua City).",
    body2:
      "The manufacturer maintains an active bilingual corporate microsite on the platform, with declared capabilities, certifications, and contact information available for due diligence. Where applicable, supporting documents (IMMEX permit, USMCA certificate of origin, ISO certifications, capacity sheets) are held in the platform's secure compliance vault and may be shared with US buyers via time-limited, revocable share links.",
    closing:
      "Corridor Digital does not act as an agent, broker, or party to any procurement transaction. All commercial relationships are direct between buyer and manufacturer. For verification of current document status or to request a compliance share link, contact the manufacturer directly via their microsite or this office.",
    signerRole: "Founder · Corridor Digital",
    entityFooter: "Operating entity",
    footnote: "This letter is informational. It is not a guarantee of the manufacturer's certifications or business practices. Buyers should perform independent due diligence.",
  },
  es: {
    back: "Volver al panel",
    print: "Imprimir / guardar PDF",
    letterHeader: "CARTA OFICIAL DE LISTADO",
    kicker: "REF · CD-LOL-" + new Date().getFullYear(),
    title: "Carta de Listado — Registro Corridor Digital.",
    subjectLabel: "SUJETO",
    legalName: "Razón Social",
    founded: "Fundada",
    employees: "empleados",
    entityLabel: "ENTIDAD LEGAL",
    statusLabel: "ESTATUS EN PLATAFORMA",
    statusVerified: "Corredor Verificado",
    statusUnverified: "Listado en proceso",
    tier: "Plan",
    published: "Publicado",
    profileUrl: "URL del perfil",
    capLabel: "CAPACIDADES DECLARADAS",
    certsLabel: "CERTIFICACIONES DECLARADAS",
    padron: "Padrón #",
    body1:
      "A quien corresponda — la presente confirma que {name} es un miembro listado del registro Corridor Digital, el directorio bilingüe IMMEX oficial para el corredor de manufactura Chihuahua–El Paso (Juárez, Parral, Chihuahua City).",
    body2:
      "El fabricante mantiene un micrositio corporativo bilingüe activo en la plataforma, con capacidades declaradas, certificaciones e información de contacto disponibles para due diligence. Cuando aplica, los documentos soporte (permiso IMMEX, certificado USMCA de origen, certificaciones ISO, hojas de capacidad) se conservan en la bóveda segura de cumplimiento de la plataforma y pueden compartirse con compradores en EE.UU. mediante enlaces caducos y revocables.",
    closing:
      "Corridor Digital no actúa como agente, intermediario ni parte en ninguna transacción de compra. Todas las relaciones comerciales son directas entre comprador y fabricante. Para verificar el estatus actual de documentos o solicitar un enlace de cumplimiento, contacta al fabricante directamente vía su micrositio o esta oficina.",
    signerRole: "Fundador · Corridor Digital",
    entityFooter: "Entidad operadora",
    footnote: "Esta carta es informativa. No constituye garantía de las certificaciones o prácticas comerciales del fabricante. Los compradores deben realizar due diligence independiente.",
  },
};
